import { defineStore } from 'pinia';
import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import { SortField } from '@/composables/usePerformanceDashboardFilters';
import { captureException } from '@/errors';
import { initStore } from '@/stores/store-utils';
import {
  ClosedCoverageMetrics,
  ClosedLostSummary,
  ClosedWon,
  EcosystemMetrics,
  EqlsWithEngagementSummary,
  LeadGenerationCount,
  OpenCoverageMetrics,
  OpenOpportunitiesSummary,
  TeamEngagementSummary,
  UserEngagementBreakdown,
  UserEngagementSummary,
} from '@/types/ecosystem_insights';

const defaultEcosystemMetrics = {
  deal_size_with_partners: null,
  deal_size_without_partners: null,
  time_to_close_with_partners: null,
  time_to_close_without_partners: null,
  win_rate_with_partners: null,
  win_rate_without_partners: null,
};

export type PerformanceMetricsTimePeriod =
  | 'this_quarter'
  | 'last_quarter'
  | 'year_to_date';

export type PipelineMetricsBusinessType = 'all' | 'new_business' | 'existing';

const SORT_FIELD_TO_QUERY_PARAM = {
  overlapCount: 'overlap_count',
  engagementActivitiesCount: 'engagement_activities_count',
  partnerImpact: 'partner_impact',
  partnerScore: 'partner_score',
  attributionAmount: 'attribution_amount',
} as const;

export const useEcosystemInsightsStore = defineStore(
  'EcosystemInsightsStore',
  () => {
    const ecosystemMetrics = ref<EcosystemMetrics>(defaultEcosystemMetrics);
    const currentQuarterMetrics = ref<EcosystemMetrics>(
      defaultEcosystemMetrics,
    );
    const currentOpenCoverageMetrics = ref<OpenCoverageMetrics | undefined>(
      undefined,
    );
    const currentTotalClosedWonMetrics = ref<ClosedWon | undefined>(undefined);
    const currentClosedCoverageMetrics = ref<ClosedCoverageMetrics | undefined>(
      undefined,
    );
    const currentTeamEngagementSummary = ref<TeamEngagementSummary | undefined>(
      undefined,
    );
    const currentClosedLostSummary = ref<ClosedLostSummary | undefined>(
      undefined,
    );
    const currentLeadGenerationCount = ref<LeadGenerationCount | undefined>(
      undefined,
    );
    const currentOpenOpportunitiesSummary = ref<
      OpenOpportunitiesSummary | undefined
    >(undefined);
    const currentEqlsWithEngagementSummary = ref<
      EqlsWithEngagementSummary | undefined
    >(undefined);
    const currentUserEngagementSummary = ref<UserEngagementSummary | undefined>(
      undefined,
    );
    const currentUserEngagementBreakdown = ref<
      UserEngagementBreakdown | undefined
    >(undefined);

    const { initError, ready, readySync, running, refresh } = initStore(
      async () => {
        const { data, error } = await crossbeamApi.GET(
          '/v0.1/ecosystem-metrics',
        );

        if (error) throw error;
        if (data) ecosystemMetrics.value = data;
      },
    );

    async function initOpenCoverageMetrics({
      opportunityType,
      dealOwnerIds,
      dealProductType,
    }: {
      opportunityType?: 'net-new' | 'existing';
      dealOwnerIds?: string[];
      dealProductType?: string[];
    } = {}) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/open-coverage',
        {
          params: {
            query: {
              ...(opportunityType && { opportunity_type: opportunityType }),
              ...(dealOwnerIds && { deal_owner_id: dealOwnerIds }),
              ...(dealProductType && { deal_product_type: dealProductType }),
            },
          },
        },
      );
      if (error) captureException(error);
      if (data) currentOpenCoverageMetrics.value = data;
    }

    async function initTotalClosedWonMetrics({
      endDate,
      opportunityType,
      startDate,
      dealOwnerIds,
      dealProductType,
    }: {
      endDate?: string;
      opportunityType?: 'net-new' | 'existing';
      startDate: string;
      dealOwnerIds?: string[];
      dealProductType?: string[];
    }) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/closed-won/summary',
        {
          params: {
            query: {
              ...(endDate && { end_date: endDate }),
              start_date: startDate,
              opportunity_type: opportunityType,
              ...(dealOwnerIds && { deal_owner_id: dealOwnerIds }),
              ...(dealProductType && { deal_product_type: dealProductType }),
            },
          },
        },
      );
      if (error) captureException(error);
      if (data) currentTotalClosedWonMetrics.value = data;
    }

    async function initClosedCoverageMetrics({
      coverageType,
      endDate,
      opportunityType,
      startDate,
      dealOwnerIds,
      dealProductType,
    }: {
      coverageType: 'partners' | 'engagement';
      endDate?: string;
      opportunityType?: 'net-new' | 'existing';
      startDate: string;
      dealOwnerIds?: string[];
      dealProductType?: string[];
    }) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/closed-coverage/{coverage_type}',
        {
          params: {
            path: { coverage_type: coverageType },
            query: {
              ...(endDate && { end_date: endDate }),
              start_date: startDate,
              opportunity_type: opportunityType,
              deal_owner_id: dealOwnerIds,
              ...(dealProductType && { deal_product_type: dealProductType }),
            },
          },
        },
      );
      if (error) captureException(error);
      if (data) currentClosedCoverageMetrics.value = data;
    }

    async function initTeamEngagementSummary({
      endDate,
      startDate,
      dealOwnerIds,
    }: {
      endDate?: string;
      startDate: string;
      dealOwnerIds?: string[];
    }) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/team-engagement/summary',
        {
          params: {
            query: {
              ...(endDate && { end_date: endDate }),
              ...(dealOwnerIds && { deal_owner_id: dealOwnerIds }),
              start_date: startDate,
            },
          },
        },
      );
      if (error) captureException(error);
      if (data) currentTeamEngagementSummary.value = data;
    }

    async function fetchTeamEngagementSummaryYTD(startDate: string) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/team-engagement/summary',
        {
          params: {
            query: {
              start_date: startDate,
            },
          },
        },
      );
      if (error) throw error;
      if (data) return data;
    }

    async function fetchTeamEngagement({
      dealOwnerIds,
      startDate,
      endDate,
      limit,
      page,
    }: {
      startDate: string;
      endDate?: string;
      limit?: number;
      page?: number;
      dealOwnerIds?: string[];
    }) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/team-engagement',
        {
          params: {
            query: {
              start_date: startDate,
              ...(endDate && { end_date: endDate }),
              ...(limit ? { limit } : {}),
              ...(page ? { page } : {}),
              ...(dealOwnerIds && { deal_owner_id: dealOwnerIds }),
            },
          },
        },
      );
      if (error) throw error;
      if (data) return data;
    }

    async function fetchUserEngagementSummary({
      startDate,
      endDate,
    }: {
      startDate: string;
      endDate?: string;
    }) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/user-engagement/summary',
        {
          params: {
            query: {
              start_date: startDate,
              ...(endDate && { end_date: endDate }),
            },
          },
        },
      );
      if (error) captureException(error);
      if (data) currentUserEngagementSummary.value = data;
    }

    async function fetchUserEngagement({
      startDate,
      endDate,
      limit,
      page,
    }: {
      startDate: string;
      endDate?: string;
      limit?: number;
      page?: number;
    }) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/user-engagement',
        {
          params: {
            query: {
              start_date: startDate,
              ...(endDate && { end_date: endDate }),
              ...(limit ? { limit } : {}),
              ...(page ? { page } : {}),
            },
          },
        },
      );
      if (error) throw error;
      if (data) return data;
    }

    async function fetchClosedLost({
      startDate,
      endDate,
      limit,
      page,
      ownerIds,
      partnerOrgUuids,
      opportunityType,
      dealProductType,
    }: {
      startDate: string;
      endDate?: string;
      limit?: number;
      page?: number;
      ownerIds?: string[];
      partnerOrgUuids?: string[];
      opportunityType?: 'net-new' | 'existing';
      dealProductType?: string[];
    }) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/closed-lost',
        {
          params: {
            query: {
              start_date: startDate,
              ...(endDate && { end_date: endDate }),
              ...(limit ? { limit } : {}),
              ...(page ? { page } : {}),
              ...(ownerIds ? { deal_owner_id: ownerIds } : {}),
              ...(partnerOrgUuids && { partner_org_uuid: partnerOrgUuids }),
              ...(opportunityType && { opportunity_type: opportunityType }),
              ...(dealProductType && { deal_product_type: dealProductType }),
            },
          },
        },
      );
      if (error) throw error;
      if (data) return data;
    }

    async function fetchPartnerLeaderboard({
      startDate,
      endDate,
      sort,
      limit,
      page,
      partnerTagIds,
    }: {
      startDate: string;
      endDate?: string;
      sort?: SortField;
      limit?: number;
      page?: number;
      partnerTagIds?: string[];
    }) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/partner-leaderboard',
        {
          params: {
            query: {
              start_date: startDate,
              ...(endDate && { end_date: endDate }),
              ...(sort && { sort: SORT_FIELD_TO_QUERY_PARAM[sort] }),
              ...(sort && { order: 'desc' }),
              ...(limit ? { limit } : {}),
              ...(page ? { page } : {}),
              ...(partnerTagIds?.length && { partner_tag_id: partnerTagIds }),
            },
          },
        },
      );
      if (error) throw error;
      if (data) return data;
    }

    async function initClosedLostSummary({
      endDate,
      startDate,
      dealOwnerIds,
      partnerOrgUuids,
      opportunityType,
      dealProductType,
    }: {
      endDate?: string;
      startDate: string;
      dealOwnerIds?: string[];
      partnerOrgUuids?: string[];
      opportunityType?: 'net-new' | 'existing';
      dealProductType?: string[];
    }) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/closed-lost/summary',
        {
          params: {
            query: {
              ...(endDate && { end_date: endDate }),
              ...(dealOwnerIds && { deal_owner_id: dealOwnerIds }),
              ...(partnerOrgUuids && { partner_org_uuid: partnerOrgUuids }),
              ...(opportunityType && { opportunity_type: opportunityType }),
              ...(dealProductType && { deal_product_type: dealProductType }),
              start_date: startDate,
            },
          },
        },
      );
      if (error) captureException(error);
      if (data) currentClosedLostSummary.value = data;
    }

    async function initLeadGenerationCount({
      partnerOrgUuids,
      ownerId,
      opportunityType,
      dealOwnerIds,
    }: {
      partnerOrgUuids?: string[];
      ownerId?: string[];
      opportunityType?: 'net-new' | 'existing';
      dealOwnerIds?: string[];
    } = {}) {
      const { data, error } = await crossbeamApi.POST(
        '/v0.1/records/deal-nav/lead-generation-count/search',
        {
          body: {
            ...(partnerOrgUuids?.length && {
              partner_org_uuid: partnerOrgUuids,
            }),
            ...(ownerId?.length && { owner_id: ownerId }),
            ...(opportunityType && { opportunity_type: opportunityType }),
            ...(dealOwnerIds?.length && { deal_owner_id: dealOwnerIds }),
          },
        },
      );
      if (error) captureException(error);
      if (data) currentLeadGenerationCount.value = data;
    }

    async function initOpenOpportunitiesSummary({
      opportunityType,
      dealOwnerIds,
      startDate,
      endDate,
    }: {
      opportunityType?: 'net-new' | 'existing';
      dealOwnerIds?: string[];
      startDate?: string;
      endDate?: string;
    } = {}) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/pipeline-generated/summary',
        {
          params: {
            query: {
              ...(opportunityType && { opportunity_type: opportunityType }),
              ...(dealOwnerIds && { deal_owner_id: dealOwnerIds }),
              ...(startDate && { start_date: startDate }),
              ...(endDate && { end_date: endDate }),
            },
          },
        },
      );
      if (error) captureException(error);
      if (data) currentOpenOpportunitiesSummary.value = data;
    }

    async function initEqlsWithEngagementSummary({
      partnerOrgUuids,
      ownerId,
      opportunityType,
      dealOwnerIds,
    }: {
      partnerOrgUuids?: string[];
      ownerId?: string[];
      opportunityType?: 'net-new' | 'existing';
      dealOwnerIds?: string[];
    } = {}) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/pipeline-prospects/summary',
        {
          params: {
            query: {
              ...(partnerOrgUuids && { partner_org_uuid: partnerOrgUuids }),
              ...(ownerId && { owner_id: ownerId }),
              ...(opportunityType && { opportunity_type: opportunityType }),
              ...(dealOwnerIds && { deal_owner_id: dealOwnerIds }),
            },
          },
        },
      );
      if (error) captureException(error);
      if (data) currentEqlsWithEngagementSummary.value = data;
    }

    async function fetchUserEngagementBreakdown({
      endDate,
      startDate,
    }: {
      endDate?: string;
      startDate: string;
    }) {
      const { data, error } = await crossbeamApi.GET(
        '/v0.2/ecosystem-metrics/user-engagement/breakdown',
        {
          params: {
            query: {
              start_date: startDate,
              ...(endDate && { end_date: endDate }),
            },
          },
        },
      );
      if (error) captureException(error);
      if (data) currentUserEngagementBreakdown.value = data;
    }

    function clearUserEngagementBreakdown() {
      currentUserEngagementBreakdown.value = undefined;
    }
    refresh({ useCache: true });

    return {
      currentClosedCoverageMetrics,
      currentClosedLostSummary,
      currentEqlsWithEngagementSummary,
      currentLeadGenerationCount,
      currentOpenOpportunitiesSummary,
      currentTeamEngagementSummary,
      currentUserEngagementBreakdown,
      error: initError,
      fetchClosedLost,
      fetchTeamEngagement,
      fetchUserEngagement,
      fetchUserEngagementBreakdown,
      clearUserEngagementBreakdown,
      ready,
      readySync,
      running,
      refresh,
      ecosystemMetrics,
      initClosedLostSummary,
      initEqlsWithEngagementSummary,
      initLeadGenerationCount,
      initOpenOpportunitiesSummary,
      currentQuarterMetrics,
      initClosedCoverageMetrics,
      initOpenCoverageMetrics,
      initTeamEngagementSummary,
      initTotalClosedWonMetrics,
      currentOpenCoverageMetrics,
      currentTotalClosedWonMetrics,
      fetchTeamEngagementSummaryYTD,
      fetchPartnerLeaderboard,
      fetchUserEngagementSummary,
      currentUserEngagementSummary,
    };
  },
);
